import styled, { StyledProps } from 'styled-components/macro';
import { IconStyled } from 'components/Icons/IconStyles';

export type ButtonStylesProps = StyledProps<{
  variant: 'primary' | 'secondary' | 'tertiary' | 'icon' | 'icon-light' | 'semitransparent';
  size?: 'sm' | 'md' | 'lg';
  dense?: boolean;
  disabled: boolean;
}>;

const switchBgColor = (props: ButtonStylesProps) => {
  const options = {
    icon: 'transparent',
    'icon-light': 'transparent',
    primary: props.theme.colors.primary.main,
    secondary: props.theme.colors.white,
    semitransparent: 'rgba(255, 255, 255, 0.5)',
    tertiary: 'transparent',
  };

  return options[props.variant] || 'transparent';
};

const switchHoverBgColor = (props: ButtonStylesProps) => {
  const options = {
    icon: 'transparent',
    'icon-light': 'transparent',
    primary: props.theme.colors.primary.light,
    secondary: props.theme.colors.white,
    semitransparent: 'rgba(77, 119, 240, 0.1)',
    tertiary: 'transparent',
  };

  return options[props.variant] || 'transparent';
};

const switchTextColor = (props: ButtonStylesProps) => {
  const options = {
    icon: props.theme.colors.black,
    'icon-light': props.theme.colors.white,
    primary: props.theme.colors.white,
    secondary: props.theme.colors.black,
    semitransparent: props.theme.colors.primary.main,
    tertiary: props.theme.colors.primary.main,
  };

  return options[props.variant] || props.theme.colors.white;
};

const switchHoverTextColor = (props: ButtonStylesProps) => {
  const options = {
    icon: props.theme.colors.primary.main,
    'icon-light': props.theme.colors.primary.accent,
    primary: props.theme.colors.white,
    secondary: props.theme.colors.primary.main,
    semitransparent: props.theme.colors.primary.main,
    tertiary: props.theme.colors.primary.dark,
  };

  return options[props.variant] || props.theme.colors.white;
};

const switchMinWidth = (props: ButtonStylesProps) => {
  if (props.variant === 'icon' || props.variant === 'icon-light') {
    return 'auto';
  }

  if (!props.size) {
    return 'auto';
  }

  const options = {
    lg: '158px',
    md: '110px',
    sm: 'auto',
  };
  return options[props.size];
};

const fontWeight = {
  icon: 400,
  'icon-light': 400,
  primary: 500,
  secondary: 400,
  semitransparent: 400,
  tertiary: 500,
};

export const Button = styled.button<ButtonStylesProps>`
  border: 1px solid;
  display: inline-flex;
  box-shadow: none;
  background-color: ${switchBgColor};
  border-radius: 4px;
  font-size: ${(props) =>
    (props.variant === 'icon' || props.variant) && props.size === 'lg' ? '1.2rem' : '0.875rem'};
  letter-spacing: -0.18px;
  text-align: center;
  vertical-align: middle;
  padding: ${(props) => (props.dense ? '1px 5px' : '1px 12px')};
  line-height: ${(props) =>
    props.variant === 'icon' || props.variant === 'icon-light' ? '24px' : ''};
  font-weight: ${(props) => fontWeight[props.variant] || 400};
  align-items: center;
  justify-content: center;
  color: ${switchTextColor};
  min-width: ${switchMinWidth};
  height: ${(props) => (props.size !== 'sm' ? '28px' : 'auto')};
  white-space: nowrap;
  pointer-events: ${(props) => (props.disabled ? 'none' : 'inherit')};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  &:focus {
    outline: none;
  }

  & > ${IconStyled} {
    margin-right: ${(props) =>
      props.variant !== 'icon' && props.variant !== 'icon-light' ? '4px' : 0};
  }

  &:hover {
    color: ${switchHoverTextColor};
    background-color: ${switchHoverBgColor};
    opacity: ${(props) => (props.disabled ? 0.5 : 1)};
    border: ${(props) =>
      props.variant === 'secondary'
        ? `1px solid ${props.theme.colors.primary.main}`
        : '1px solid transparent'};
  }
`;
